/**
 * New evaluation template. Template example: NextAdmin `profile/account/page.tsx` (`FormPage` + `FormSection` +
 * `FormActions`), demo `/forms/form-layout`.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import {
  createEvaluationTemplateMutation,
  listEvaluationTemplatesQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zEvaluationTemplateCreate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { useToast } from '../../components/ui/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import type { EvaluationSubjectKind } from '../../services/api/evaluation-template.api.js';
import { SpecComposer } from './evaluation-templates/SpecComposer.js';
import type { SpecItem } from './evaluation-templates/SpecComposer.js';
import {
  SUBJECT_KIND_OPTIONS,
  TEMPLATE_KIND_OPTIONS,
  VISIBILITY_OPTIONS,
} from './evaluation-templates/template-options.js';

const LIST_PATH = '/admin/evaluation-templates';

/** The generated create schema; the spec is built from the composer below, so it is not a form field. */
const createSchema = zEvaluationTemplateCreate.omit({ spec: true });

export const NewEvaluationTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createEvaluationTemplateMutation());
  const [specItems, setSpecItems] = useState<SpecItem[]>([]);
  const form = useZodForm(createSchema, {
    defaultValues: { templateRef: '', name: '', description: '', templateKind: 'leaf', visibility: 'org' },
  });
  const subjectKind = useWatch({ control: form.control, name: 'subjectKind' });

  const submit = async (values: { subjectKind: EvaluationSubjectKind } & Record<string, unknown>) => {
    if (specItems.length === 0) throw new Error('Add at least one spec item.');
    const result = await create.mutateAsync({
      body: {
        templateRef: String(values.templateRef),
        name: String(values.name),
        description: values.description ? String(values.description) : null,
        subjectKind: values.subjectKind,
        templateKind: values.templateKind === 'group' ? 'group' : 'leaf',
        visibility: values.visibility === 'private' ? 'private' : 'org',
        spec: { schemaVersion: 'evaluation-template-spec/v1', subjectKind: values.subjectKind, items: specItems },
      },
    });
    await queryClient.invalidateQueries({ queryKey: listEvaluationTemplatesQueryKey() });
    notify('Template created');
    navigate(`${LIST_PATH}?template=${result.data.id}`);
  };

  return (
    <Can policy="CanManageEvaluationTemplates">
      <FormPage
        title="New evaluation template"
        breadcrumbs={[{ label: 'Evaluation templates', to: LIST_PATH }, { label: 'New' }]}
        backTo={LIST_PATH}
        backLabel="Evaluation templates"
      >
        <Form form={form} onSubmit={submit} className="space-y-8">
          <FormSection title="Template">
            <TextFormField
              name="templateRef"
              label="Template ref"
              placeholder="e.g. base-strategy-checks"
              description="URL-safe identifier, unique per organization"
              required
            />
            <TextFormField name="name" label="Name" placeholder="e.g. Base Strategy Checks" required />
            <SelectFormField
              name="subjectKind"
              label="Subject kind"
              options={SUBJECT_KIND_OPTIONS}
              placeholder="Select a kind"
              required
            />
            <SelectFormField name="templateKind" label="Template kind" options={TEMPLATE_KIND_OPTIONS} required />
            <SelectFormField name="visibility" label="Visibility" options={VISIBILITY_OPTIONS} required />
            <FormFullWidth>
              <TextAreaFormField name="description" label="Description" rows={2} placeholder="Optional" />
            </FormFullWidth>
          </FormSection>

          <FormSection title="Initial spec items">
            <FormFullWidth>
              <SpecComposer subjectKind={subjectKind ?? ''} items={specItems} onChange={setSpecItems} />
            </FormFullWidth>
          </FormSection>

          <FormActions submitLabel="Create template" onCancel={() => navigate(LIST_PATH)} />
        </Form>
      </FormPage>
    </Can>
  );
};

export default NewEvaluationTemplatePage;
